/**
 * Client config Zod schema — validates whitelabel configuration.
 * Every field has a default so partial configs work.
 */
import { z } from 'zod'

const HexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/)

const ColorTriad = z.object({
  DEFAULT: HexColor,
  light: HexColor,
  dark: HexColor,
})

export const ClientConfigSchema = z.object({
  // ── Brand Identity ──────────────────────────────────────────────────────
  brand: z.object({
    name: z.string().min(1).max(30),
    meaning: z.string().max(60).default(''),
    tagline: z.string().max(120).default(''),
    service: z.string().max(60).default(''),
    version: z.string().default('0.1.0'),
    contact: z
      .object({
        whatsapp: z.string().optional(),
        whatsappLink: z.string().url().optional(),
        email: z.string().email().optional(),
        phone: z.string().optional(),
        website: z.string().url().optional(),
      })
      .default({}),
  }),

  // ── Colors ──────────────────────────────────────────────────────────────
  colors: z.object({
    primary: ColorTriad,
    secondary: ColorTriad,
    accent: ColorTriad,
    gradient: z.object({
      start: HexColor,
      middle: HexColor,
      end: HexColor,
    }),
    status: z.object({
      success: HexColor,
      warning: HexColor,
      error: HexColor,
      info: HexColor,
    }),
  }),

  // ── Typography ──────────────────────────────────────────────────────────
  fonts: z.object({
    heading: z.string(),
    body: z.string(),
    bodyBold: z.string(),
    mono: z.string(),
  }),

  spacing: z.object({
    xs: z.number(),
    sm: z.number(),
    md: z.number(),
    lg: z.number(),
    xl: z.number(),
    '2xl': z.number(),
    '3xl': z.number(),
  }),

  radius: z.object({
    xs: z.number(),
    sm: z.number(),
    md: z.number(),
    lg: z.number(),
    xl: z.number(),
    full: z.number(),
  }),
})

export type ClientConfig = z.infer<typeof ClientConfigSchema>
